import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getBusinessDashboard } from '../../../../Services/CRMAdminServices/CRMDashboardService';
import Scoreboard from './Scoreboard';
import { getSoniaScoreboardService } from '../../../../Services/CRMAdminServices/CRMOperator';

/*
  ONE BUSINESS, ONE SCREEN.

  Two of these are mounted at two places in the menu - Dashboard - Fintrack and
  Dashboard - Business Builder - because they are two businesses and the owner
  manages them separately. Neither is a tab of the other.

  Look (Richard, 13 Oct 2026, from fintrack-dashboards.html): a light workspace.
  Four headline numbers against the period before, then "where to focus" beside
  the pipeline, then money beside Sonia. Every focus item has a button that goes
  to the list where the work is done.
*/

type Tile = { name: string; actual: number; target: number; status: string; note: string };
type FunnelStep = { name: string; stageCode?: string; modelPct: number; expectedFrom100: number; actual: number; hasStage: boolean };
type Outcome = { name: string; amount: number; count: number; kind: string; note: string; hasStage: boolean };
type Grid = { title: string; columns: string[]; rows: string[][]; note: string };
type Trend = { label: string; top: number; middle: number; wins: number; money: number };
type Source = { name: string; kind: string; amount: number };
type Money = {
    payingClients: number; recurring: number; revenue: number; costOfDelivery: number;
    margin: number; messagesSent: number; messagesCost: number; sources: Source[]; note: string;
    xeroConnected: boolean; expenses: number; netProfit: number; xeroNote: string;
};
type Panel = {
    name: string; unitsTotal: number; cohortSize: number;
    tiles: Tile[]; funnel: FunnelStep[]; outcomes: Outcome[]; table: Grid; trend: Trend[];
    commissionPerDeal: number;
    headlineLabel: string; headlinePct: string;
    roadblocksTotal: number; winsCount: number; winsLabel: string; note?: string;
};
type Resp = {
    fromDate: string; toDate: string; stuckDays: number; leadsUnassigned: number;
    businesses: Panel[]; monthLabel: string; money: Money; note?: string;
};

/* Palette from the approved design (fintrack-dashboards.html). */
const BG = '#f3f5fa', PANEL = '#fff', INK = '#172643', SUB = '#63708a', LINE = '#e6eaf2';
const BLUE = '#304fe4', SOFT = '#eef1ff', AMBER = '#fff4df', WARN = '#915b05';
const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
const PUB = process.env.PUBLIC_URL || '';

const CSS = `
.ftd{font-family:${FONT};font-size:14px;line-height:1.5;color:${INK};background:${BG};min-height:100%}
.ftd *{box-sizing:border-box}
.ftd h1,.ftd h2,.ftd p{margin:0}
.ftd button{font:inherit;cursor:pointer}
.ftd-top{background:${PANEL};border-bottom:1px solid ${LINE};padding:16px 26px;display:flex;align-items:center;justify-content:space-between;gap:16px;flex-wrap:wrap}
.ftd-brand{font-size:21px;font-weight:750;letter-spacing:-.8px;color:${INK}}
.ftd-brand i{display:inline-block;width:21px;height:21px;border-radius:50%;background:#f6c747;margin-right:9px;vertical-align:middle}
.ftd-brand small{font-size:12px;font-weight:400;letter-spacing:0;margin-left:9px;color:${SUB}}
.ftd-tabs{display:flex;gap:5px;background:${BG};padding:5px;border-radius:10px;flex-wrap:wrap}
.ftd-tabs a,.ftd-tabs button{border:0;border-radius:7px;padding:8px 15px;color:${SUB};background:transparent;text-decoration:none;font-size:13.5px}
.ftd-tabs a:hover,.ftd-tabs button:hover{color:${INK}}
.ftd-tabs .on{background:${INK};color:${PANEL}!important}
.ftd-refresh{border:1px solid ${LINE};border-radius:8px;padding:8px 12px;background:${PANEL};color:${INK};font-size:12.5px}
.ftd-refresh:disabled{color:${SUB};cursor:default}
.ftd main{padding:26px}
.ftd-heading{display:flex;justify-content:space-between;align-items:center;gap:15px;margin-bottom:23px;flex-wrap:wrap}
.ftd-kicker{font-size:11px;letter-spacing:1.6px;text-transform:uppercase;color:${BLUE};font-weight:700}
.ftd h1{font-size:27px;letter-spacing:-1px;line-height:1.2;margin:4px 0 7px;font-weight:650;color:${INK}}
.ftd-muted{color:${SUB};font-size:12px}
.ftd-when{display:flex;gap:10px;align-items:center;flex-wrap:wrap}
.ftd-date{border:1px solid ${LINE};border-radius:8px;padding:9px 12px;background:${PANEL};font-size:12px}
.ftd-metrics{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;margin-bottom:20px}
.ftd-metric{background:${PANEL};padding:18px;border:1px solid ${LINE};border-radius:12px}
.ftd-metric label{font-size:12px;color:${SUB};margin:0}
.ftd-number{font-size:32px;letter-spacing:-1px;font-weight:650;font-variant-numeric:tabular-nums;margin:6px 0;line-height:1.2}
.ftd-grid{display:grid;grid-template-columns:1.2fr 1fr;gap:18px;align-items:start}
.ftd-wide{margin-top:18px}
.ftd-panel{border:1px solid ${LINE};background:${PANEL};border-radius:12px;overflow:hidden}
.ftd-head{padding:18px 20px;border-bottom:1px solid ${LINE};display:flex;justify-content:space-between;align-items:center;gap:10px}
.ftd h2{font-size:15px;font-weight:650;color:${INK}}
.ftd-content{padding:18px 20px}
.ftd-task{padding:18px 20px;border-bottom:1px solid ${LINE}}
.ftd-taskline{display:flex;justify-content:space-between;gap:16px;align-items:center;margin-bottom:5px}
.ftd-task strong{font-size:23px;font-variant-numeric:tabular-nums;font-weight:650}
.ftd-alert{background:${AMBER};color:${WARN};border-radius:6px;padding:4px 8px;font-size:11px;white-space:nowrap}
.ftd-calm{background:${SOFT};color:${BLUE};border-radius:6px;padding:4px 8px;font-size:11px;white-space:nowrap}
.ftd-action{display:inline-block;margin-top:12px;padding:7px 11px;border-radius:7px;background:${SOFT};color:${BLUE}!important;border:0;font-weight:550;font-size:13px;text-decoration:none!important}
.ftd-action:hover{background:#e2e7ff}
.ftd-stage{display:flex;align-items:center;justify-content:space-between;gap:14px;padding:10px 0;border-bottom:1px solid ${LINE}}
.ftd-stage:last-of-type{border-bottom:0}
.ftd-stage span{display:flex;align-items:center;gap:9px}
.ftd-dot{width:7px;height:7px;background:${BLUE};border-radius:50%;display:inline-block}
.ftd-gap{width:7px;height:7px;display:inline-block}
.ftd-stage strong,.ftd-row strong{font-variant-numeric:tabular-nums;font-weight:650}
.ftd-mini{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;padding:18px 20px}
.ftd-mini.two{grid-template-columns:repeat(2,minmax(0,1fr))}
.ftd-mini b{display:block;font-size:21px;margin-top:5px;font-weight:650;font-variant-numeric:tabular-nums}
.ftd-row{display:flex;justify-content:space-between;padding:9px 0;gap:12px}
.ftd details{margin-top:12px;font-size:12px;color:${SUB}}
.ftd summary{cursor:pointer}
.ftd-link{border:0;background:none;color:${BLUE};font-weight:550;font-size:12.5px;padding:0}
.ftd-err{background:#fdecec;color:#a02128;border-radius:10px;padding:12px 16px;margin-bottom:16px}
.ftd-note{font-size:11px;color:${SUB};padding-top:17px}
@media(max-width:900px){.ftd-grid{grid-template-columns:1fr}.ftd-metrics{grid-template-columns:repeat(2,1fr)}}
@media(max-width:600px){.ftd main{padding:17px}.ftd-top{padding:14px 17px}.ftd-mini,.ftd-mini.two{grid-template-columns:1fr}.ftd-number{font-size:27px}}
`;

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const money0 = (n: number) => '$' + Math.round(n ?? 0).toLocaleString();

/* The payload arrives as { success, data }; older shapes came back flat. */
const r_unwrap = (r: any) => (r?.data && r.data.data !== undefined ? r.data.data : r?.data) || null;

/* No data is still an answer, and the answer is zero. The page draws the same
   shape whatever comes back, so a quiet month looks like a quiet month rather
   than like something broken. */
const STEPS: Record<string, string[]> = {
    property: ['Enquiry', 'Property Strategy Session', 'Property Presentation', 'EOI', 'Unconditional'],
    managed: ['Gameplan Completed', 'Finance Enquiry', 'Enquiry Held', 'Application', 'Approval', 'Settlement'],
};
const MONEY_TILES: Record<string, string[]> = {
    property: ['Commission 1 - unconditional COS', 'Commission 2 - frame', 'Commission this period', 'Still to come'],
    managed: ['Signed management clients', 'Active paying brokers', 'Monthly management fees', 'Clients referred by brokers'],
};
const GRID: Record<string, { title: string; columns: string[] }> = {
    property: { title: 'Stage ageing - where deals are going cold', columns: ['Stage', 'Deals', 'Over 14 days', 'Oldest (days)'] },
    managed: { title: 'Broker activity - who is actually using it', columns: ['Broker', 'Referred clients', 'Applications', 'Settlements', 'Last activity'] },
};

function blank(which: 'property' | 'managed'): Panel {
    const steps = STEPS[which];
    const now = new Date();
    return {
        name: which === 'property' ? 'Fintrack Property' : 'MB Managed',
        unitsTotal: 0, cohortSize: 0,
        tiles: steps.slice(0, 4).map(n => ({ name: n, actual: 0, target: 0, status: 'none', note: 'nothing recorded' })),
        funnel: steps.map(n => ({ name: n, modelPct: 0, expectedFrom100: 0, actual: 0, hasStage: false })),
        outcomes: MONEY_TILES[which].map((n, i) => ({
            name: n, amount: 0, count: 0,
            kind: which === 'property' || i === 2 ? 'money' : 'count',
            note: 'nothing recorded', hasStage: false,
        })),
        table: { ...GRID[which], rows: [], note: 'Nothing recorded for this month.' },
        trend: Array.from({ length: 6 }, (_, i) => ({
            label: new Date(now.getFullYear(), now.getMonth() - 5 + i, 1).toLocaleString(undefined, { month: 'short' }),
            top: 0, middle: 0, wins: 0, money: 0,
        })),
        commissionPerDeal: 30000,
        headlineLabel: '', headlinePct: '-',
        roadblocksTotal: 0, winsCount: 0, winsLabel: '',
    };
}

type PeriodId = 'today' | 'week' | 'month' | 'year';
const PERIODS: { id: PeriodId; label: string }[] = [
    { id: 'today', label: 'Today' }, { id: 'week', label: 'Week' }, { id: 'month', label: 'Month' }, { id: 'year', label: 'Year' },
];
function rangeFor(id: PeriodId, back = 0): { from: Date; to: Date; label: string } {
    const now = new Date();
    const day = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const fmt = (d: Date) => d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
    if (id === 'today') {
        const d = new Date(day); d.setDate(d.getDate() - back);
        return { from: d, to: d, label: back ? 'yesterday' : d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) };
    }
    if (id === 'week') {
        const mon = new Date(day); mon.setDate(mon.getDate() - ((mon.getDay() + 6) % 7) - 7 * back);
        const sun = new Date(mon); sun.setDate(sun.getDate() + 6);
        const to = back ? sun : day;
        return { from: mon, to, label: back ? 'last week' : `${fmt(mon)} - ${fmt(sun)}` };
    }
    if (id === 'month') {
        const from = new Date(now.getFullYear(), now.getMonth() - back, 1);
        const end = new Date(from.getFullYear(), from.getMonth() + 1, 0);
        return { from, to: back ? end : day, label: back ? 'last month' : from.toLocaleString('en-AU', { month: 'long', year: 'numeric' }) };
    }
    const from = new Date(now.getFullYear() - back, 0, 1);
    const end = new Date(from.getFullYear(), 11, 31);
    return { from, to: back ? end : day, label: back ? 'last year' : String(from.getFullYear()) };
}
const WORD: Record<PeriodId, string> = { today: 'Today', week: 'This week', month: 'This month', year: 'This year' };

const BusinessDash: React.FC<{ which: 'property' | 'managed'; title: string; onSwitch?: (w: 'property' | 'managed') => void }> = ({ which, onSwitch }) => {
    const [period, setPeriod] = useState<PeriodId>('week');
    const [data, setData] = useState<Resp | null>(null);
    const [prev, setPrev] = useState<Resp | null>(null);
    const [busy, setBusy] = useState(true);
    const [err, setErr] = useState('');
    const [updated, setUpdated] = useState('');
    const [showScore, setShowScore] = useState(false);
    const [sonia, setSonia] = useState<any>(null);

    const cur = useMemo(() => rangeFor(period), [period]);
    const before = useMemo(() => rangeFor(period, 1), [period]);

    const load = useCallback(async () => {
        setBusy(true); setErr('');
        try {
            const [a, b] = await Promise.all([
                getBusinessDashboard(iso(cur.from), iso(cur.to), 14),
                getBusinessDashboard(iso(before.from), iso(before.to), 14).catch(() => null),
            ]);
            setData(r_unwrap(a)); setPrev(b ? r_unwrap(b) : null);
            setUpdated(new Date().toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }));
        } catch (e: any) {
            setErr(e?.response?.data?.message || e?.message || 'The dashboard could not be loaded.');
        } finally { setBusy(false); }
    }, [cur.from, cur.to, before.from, before.to]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        let alive = true;
        getSoniaScoreboardService({ audience: which === 'managed' ? 'brokers' : 'clients' })
            .then((r: any) => { if (alive) setSonia(r?.data?.ok === false ? null : r?.data); })
            .catch(() => { if (alive) setSonia(null); });
        return () => { alive = false; };
    }, [which]);

    const mb = which === 'managed';
    const wanted = mb ? 'MB Managed' : 'Fintrack Property';
    const p: Panel = { ...blank(which), ...(data?.businesses?.find(b => b.name === wanted) || {}) };
    const q: Panel = { ...blank(which), ...(prev?.businesses?.find(b => b.name === wanted) || {}) };

    const outcome = (x: Panel, n: string) => (x.outcomes || []).find(o => o.name === n);
    const tile = (x: Panel, rx: RegExp) => (x.tiles || []).find(t => rx.test(t.name));
    const fmtN = (n: number) => (n || 0).toLocaleString();
    const Before = before.label[0].toUpperCase() + before.label.slice(1);
    const vs = (now: number, then: number, show: (n: number) => string) =>
        now === then ? `Same as ${before.label}` : `${Before} ${show(then)}`;

    const fees = (x: Panel) => outcome(x, 'Monthly management fees')?.amount || 0;
    const comm = (x: Panel) => outcome(x, 'Commission this period')?.amount || 0;
    const gp = (x: Panel) => tile(x, /strategy/i)?.actual || 0;
    const props = (x: Panel) => tile(x, /proposal/i)?.actual || 0;
    const metrics = mb
        ? [
            { label: 'Management fees', value: money0(fees(p)), was: vs(fees(p), fees(q), money0) },
            { label: 'New brokers', value: fmtN(p.cohortSize), was: vs(p.cohortSize, q.cohortSize, fmtN) },
            { label: 'Proposals sent', value: fmtN(props(p)), was: vs(props(p), props(q), fmtN) },
            { label: 'Signed', value: fmtN(p.winsCount), was: vs(p.winsCount, q.winsCount, fmtN) },
        ]
        : [
            { label: 'Commission earned', value: money0(comm(p)), was: vs(comm(p), comm(q), money0) },
            { label: 'New enquiries', value: fmtN(p.cohortSize), was: vs(p.cohortSize, q.cohortSize, fmtN) },
            { label: 'Strategy sessions', value: fmtN(gp(p)), was: vs(gp(p), gp(q), fmtN) },
            { label: 'Deals won', value: fmtN(p.winsCount), was: vs(p.winsCount, q.winsCount, fmtN) },
        ];

    /* Where to focus: only what is outstanding, each with the button to the list
       where the work gets done. A person waiting (Sonia's trial approvals and call
       asks) goes first. */
    const pipeline = `${PUB}/${mb ? 'CRMPipelineMB' : 'CRMPipeline'}`;
    const leads = `${PUB}/LeadsProperty`;
    const tasks: { title: string; count: number; desc: string; button: string; to: string }[] = [];
    const pend = mb ? (sonia?.pending || {}) : {};
    if ((pend.trialsWaiting || 0) > 0) tasks.push({ title: 'Approve trial agreements', count: pend.trialsWaiting,
        desc: `Approve & send is in your email${(pend.trialNames || []).length ? ` - ${pend.trialNames.join(', ')}` : ''}.`,
        button: 'Open MB pipeline →', to: pipeline });
    if ((pend.callAsks || 0) > 0) tasks.push({ title: 'Call brokers who asked', count: pend.callAsks,
        desc: 'Replied 2 to Sonia in the last 14 days. Your booking link was sent.', button: 'Open MB pipeline →', to: pipeline });
    if ((p.roadblocksTotal || 0) > 0) tasks.push({ title: mb ? 'Restart stalled brokers' : 'Restart stalled conversations', count: p.roadblocksTotal,
        desc: `${mb ? 'Brokers' : 'Leads'} with no movement for more than ${data?.stuckDays ?? 14} days`,
        button: 'Review follow-up queue →', to: mb ? pipeline : leads });
    const steps = p.funnel || [];
    if (steps[0] && steps[1] && steps[0].actual > steps[1].actual) tasks.push({
        title: mb ? `Move brokers on to ${steps[1].name.toLowerCase()}` : 'Move enquiries to a strategy session',
        count: steps[0].actual - steps[1].actual,
        desc: `At ${steps[0].name} without ${steps[1].name.toLowerCase()}`,
        button: 'Review pipeline →', to: pipeline });
    if ((data?.leadsUnassigned || 0) > 0) tasks.push({ title: 'Give leads an owner', count: data!.leadsUnassigned,
        desc: 'Leads with nobody assigned, so they are on nobody\'s list', button: 'Assign leads →', to: leads });

    const c = sonia?.review || {};
    const short: Record<string, string> = {
        'Commission 1 - unconditional COS': 'Unconditional COS', 'Commission 2 - frame': 'Frame payment',
        'Clients referred by brokers': 'Clients referred',
    };
    const cells = (p.outcomes || []).filter(o => o.name !== 'Commission this period');
    const show = (o: Outcome) => (o.kind === 'money' ? money0(o.amount) : fmtN(o.count));

    return (
        <div className='ftd'>
            <style>{CSS}</style>
            <header className='ftd-top'>
                <div className='ftd-brand'><i />FINTRACK<small>Workspace</small></div>
                {/* Richard, 16 Oct 2026: Client and MB are separate menus now - the tabs only
                    show if a screen still passes onSwitch. */}
                {onSwitch && <nav className='ftd-tabs' aria-label='Dashboard'>
                    <button type='button' aria-pressed={!mb} className={mb ? '' : 'on'} onClick={() => onSwitch('property')}>Client</button>
                    <button type='button' aria-pressed={mb} className={mb ? 'on' : ''} onClick={() => onSwitch('managed')}>MB</button>
                </nav>}
                <button type='button' className='ftd-refresh' onClick={load} disabled={busy}>
                    {busy ? 'Loading…' : updated ? `Updated ${updated} · Refresh` : 'Refresh'}
                </button>
            </header>

            <main>
                <div className='ftd-heading'>
                    <div>
                        <div className='ftd-kicker'>{mb ? 'MB managed · Broker overview' : 'Property · Client overview'}</div>
                        <h1>{mb ? 'Build your broker business.' : 'Turn enquiries into progress.'}</h1>
                        <p className='ftd-muted'>Compared with {before.label}</p>
                    </div>
                    <div className='ftd-when'>
                        <div className='ftd-tabs' role='group' aria-label='Period'>
                            {PERIODS.map(x => (
                                <button key={x.id} type='button' aria-pressed={period === x.id}
                                    className={period === x.id ? 'on' : ''} onClick={() => setPeriod(x.id)}>{x.label}</button>
                            ))}
                        </div>
                        <div className='ftd-date'>{WORD[period]} · {cur.label}</div>
                    </div>
                </div>

                {err && <div className='ftd-err'>{err}</div>}

                <div className='ftd-metrics'>
                    {metrics.map(m => (
                        <div key={m.label} className='ftd-metric'>
                            <label>{m.label}</label>
                            <div className='ftd-number'>{m.value}</div>
                            <p className='ftd-muted'>{m.was}</p>
                        </div>
                    ))}
                </div>

                <div className='ftd-grid'>
                    <section className='ftd-panel'>
                        <div className='ftd-head'>
                            <h2>{mb ? 'Broker priorities' : 'Where to focus'}</h2>
                            {tasks.length
                                ? <span className='ftd-alert'>{tasks.length} {tasks.length === 1 ? 'priority' : 'priorities'}</span>
                                : <span className='ftd-calm'>All clear</span>}
                        </div>
                        {tasks.length === 0 && <div className='ftd-content ftd-muted'>Nothing needs you right now.</div>}
                        {tasks.map(t => (
                            <div key={t.title} className='ftd-task'>
                                <div className='ftd-taskline'><h2>{t.title}</h2><strong>{fmtN(t.count)}</strong></div>
                                <p className='ftd-muted'>{t.desc}</p>
                                <Link to={t.to} className='ftd-action'>{t.button}</Link>
                            </div>
                        ))}
                        {tasks.length > 1 && <div className='ftd-content ftd-muted'>Queues may overlap; counts should not be added together.</div>}
                    </section>

                    <section className='ftd-panel'>
                        <div className='ftd-head'><h2>{mb ? 'Broker pipeline' : 'Pipeline snapshot'}</h2><span className='ftd-muted'>Current counts</span></div>
                        <div className='ftd-content'>
                            {steps.map(s => (
                                <div key={s.name} className='ftd-stage' title={s.hasStage ? '' : 'stage not set up'}>
                                    <span>{s.actual > 0 ? <i className='ftd-dot' /> : <i className='ftd-gap' />}{s.name}</span>
                                    <strong>{fmtN(s.actual)}</strong>
                                </div>
                            ))}
                            <details>
                                <summary>About these numbers</summary>
                                <p style={{ marginTop: 6 }}>Everyone standing on each stage right now, whatever period is picked. A later stage can hold more than the one before it.</p>
                            </details>
                        </div>
                    </section>
                </div>

                <div className='ftd-grid ftd-wide'>
                    {mb ? (
                        <section className='ftd-panel'>
                            <div className='ftd-head'><h2>Broker business</h2><span className='ftd-muted'>Management &amp; referrals</span></div>
                            <div className='ftd-mini two'>
                                {(p.outcomes || []).map(o => (
                                    <div key={o.name} title={o.note}><span className='ftd-muted'>{short[o.name] || o.name}</span><b>{show(o)}</b></div>
                                ))}
                            </div>
                        </section>
                    ) : (
                        <section className='ftd-panel'>
                            <div className='ftd-head'><h2>Commission outlook</h2><span className='ftd-muted'>{money0(p.commissionPerDeal || 30000)} per deal</span></div>
                            <div className='ftd-mini'>
                                {cells.map(o => (
                                    <div key={o.name} title={o.note}><span className='ftd-muted'>{short[o.name] || o.name}</span><b>{show(o)}</b></div>
                                ))}
                            </div>
                            <div className='ftd-content ftd-muted' style={{ paddingTop: 0 }}>Commission this period: {money0(comm(p))}</div>
                        </section>
                    )}

                    <section className='ftd-panel'>
                        <div className='ftd-head'>
                            <h2>{mb ? 'Broker activity & Sonia' : 'Sonia · Outreach'}</h2>
                            <span style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                <span className='ftd-muted'>{sonia?.periodLabel || 'This month'}</span>
                                <button type='button' className='ftd-link' onClick={() => setShowScore(v => !v)}>{showScore ? 'Hide detail' : 'Full scoreboard'}</button>
                            </span>
                        </div>
                        <div className='ftd-content'>
                            {sonia ? (
                                <>
                                    <div className='ftd-row'><span>Texts sent</span><strong>{fmtN(c.messagesSent)}</strong></div>
                                    <div className='ftd-row'><span>Replies</span><strong>{fmtN(c.replied)}</strong></div>
                                    <div className='ftd-row'><span>Appointments booked</span><strong>{fmtN(c.booked)}</strong></div>
                                    <div className='ftd-row'><span>Opted out</span><strong>{fmtN(c.optouts)}</strong></div>
                                    <div className='ftd-row ftd-muted'><span>Records worked</span><strong>{fmtN(c.records)}</strong></div>
                                </>
                            ) : <p className='ftd-muted'>Sonia’s numbers are not available right now.</p>}
                        </div>
                    </section>
                </div>

                {showScore && <div className='ftd-wide'><Scoreboard audience={mb ? 'brokers' : 'clients'} /></div>}

                {(p.note || data?.note) && <p className='ftd-note'>{p.note || data?.note}</p>}
            </main>
        </div>
    );
};

export default BusinessDash;
